import './Dropdowns.scss';

import { useState } from "react";
import { useRef } from "react";
import { useEffect } from "react";

import { useDispatch } from "react-redux";

import { fetchFieldOptions } from "../../slices/fieldOptionsSlice";

import AddCircle from "@mui/icons-material/AddCircle";
import Delete from "@mui/icons-material/Delete";
import Search from "@mui/icons-material/Search";
import Cancel from "@mui/icons-material/Cancel";

import { Box } from "@mui/material";
import { FormControl } from "@mui/material";
import { Select } from "@mui/material";
import { InputLabel } from "@mui/material";
import { MenuItem } from "@mui/material";
import { Autocomplete } from "@mui/material";
import { TextField } from "@mui/material";

import { IconButton } from "../Buttons/Buttons";

export const Filter = ({options, propertyLabel, onOptionClick, reset}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState('');

    useEffect(() => {
        setSelectedOption('');
    }, [reset]);

    const toggleDropdown = () => {
        setIsOpen((prevIsOpen) => !prevIsOpen);
    };

    const handleOptionClick = (option, event) => {
        if (event) {
            event.stopPropagation();
        }
        setSelectedOption(option.name);
        setIsOpen(false);
        onOptionClick(option);
    };

    return (
        <div className={"filter"} onClick={toggleDropdown}>
            <div className={"filter_label"}>
                {selectedOption || (propertyLabel)}
            </div>
            {isOpen && (
                <ul className={"filter_menu"}>
                    {options.map((option) => (
                        <li
                            key={option.id}
                            value={option.id}
                            className={"filter_menu_item"}
                            onClick={(event) => handleOptionClick(option, event)}
                        >
                            {option.name}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
}

export const Dropdown = ({ propertyLabel, options, value, onChange, fieldName, url, token, parentFieldName, parentValue }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const dispatch = useDispatch();
    const dropdownRef = useRef(null);
    const initialUrl = useRef(url);

    useEffect(() => {
        const handleClick = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClick);

        return () => {
            document.removeEventListener("mousedown", handleClick);
        }
    }, [])

    const toggleDropdown = () => setIsOpen(prev => !prev);

    const handleOptionClick = (e, option) => {
        e.stopPropagation();
        if (option.id !== value) {
            onChange(option.id);
        }
        setIsOpen(false);
    };

    const handleSearch = () => {
        if (!url) return;

        const urlObj = new URL(url, window.location.origin);

        if (parentFieldName && parentValue) {
            urlObj.searchParams.set(parentFieldName, parentValue);
        }

        urlObj.searchParams.set("search", searchValue);

        dispatch(fetchFieldOptions({
            fieldName,
            url: urlObj.toString(),
            token
        }));
    };

    const handleClear = () => {
        setSearchValue("");

        const urlObj = new URL(initialUrl.current, window.location.origin);
        if (parentFieldName && parentValue) {
            urlObj.searchParams.set(parentFieldName, parentValue);
        }

        dispatch(fetchFieldOptions({
            fieldName,
            url: urlObj.toString(),
            token
        }));
    };

    const selectedOption = options.find(option => option.id === value);

    return (
        <div className="dropdown" onClick={toggleDropdown} ref={dropdownRef}>
            <div className="dropdown_label">
                {selectedOption ? selectedOption.name : propertyLabel}
            </div>
            {isOpen && (
                <div className="dropdown_menu" onClick={(e) => e.stopPropagation()}>
                    <div className="dropdown_search">
                        <div className="dropdown_search-wrapper">
                            <input
                                className="dropdown_search-input"
                                value={searchValue}
                                type="text"
                                placeholder="Поиск"
                                onChange={(e) => setSearchValue(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleSearch();
                                    }
                                }}
                            />
                            {searchValue && (
                                <button className="dropdown_search-clear" onClick={handleClear}>
                                    <Cancel style={{fontSize: "18px"}}/>
                                </button>
                            )}
                        </div>
                        <IconButton Icon={Search} color="#3C50E0" onClick={(e) => {
                            e.stopPropagation();
                            handleSearch();
                        }} />
                    </div>

                    <ul>
                        {options.map((option) => (
                            <li
                                key={option.id}
                                className="dropdown_menu_item"
                                onClick={(e) => handleOptionClick(e, option)}
                            >
                                {option.name}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export const SearchInput = ({ value, onChange, placeholder }) => {
    const [inputValue, setInputValue] = useState(
        typeof value === "string" ? value : ""
    );

    const handleInputChange = (e) => {
        const newValue = e.target.value;
        setInputValue(newValue);
        onChange(newValue);
    };

    return (
        <div className="search-filter">
            <input
                type="text"
                className="search-filter_input"
                value={inputValue}
                onChange={handleInputChange}
                placeholder={placeholder}
            />
        </div>
    );
};


export const BooleanDropdown = ({propertyLabel, value, onChange, options = []}) => {
    const [isOpen, setIsOpen] = useState(false);

    const toggleDropdown = () => {
        setIsOpen(prev => !prev);
    }

    const handleOptionClick = (e, option) => {
        e.stopPropagation();
        onChange(option.id);
        setIsOpen(false);
    }

    const selectedOption = options.find(option => option.id === value);

    return (
        <div className={"dropdown"} onClick={toggleDropdown}>
            <div className={"dropdown_label"}>
                {selectedOption !== undefined ? selectedOption.name : propertyLabel}
            </div>
            {isOpen && (
                <ul className={"dropdown_menu"}>
                    {options.map(option => (
                        <li
                            className={"dropdown_menu_item"}
                            key={option.id}
                            onClick={(e) => handleOptionClick(e, option)}
                        >
                            {option.name}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
}

export const MultiSelectDropdown = ({
                                        onChange,
                                        options = [],
                                        propertyLabel,
                                        setAlert,
                                        fieldName,
                                        url,
                                        token,
                                        parentValue,
                                        parentFieldName
                                    }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [searchValue, setSearchValue] = useState("");
    const dispatch = useDispatch();
    const dropdownRef = useRef(null);
    const initialUrl = useRef(url);

    useEffect(() => {
        const handleClick = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener("click", handleClick);
        return () => {
            document.removeEventListener("click", handleClick);
        };
    }, []);

    const toggleDropdown = () => setIsOpen(prev => !prev);

    const handleOptionClick = (e, option) => {
        e.stopPropagation();
        const alreadySelected = selectedOptions.find((selected) => selected.id === option.id);

        if (alreadySelected) {
            setAlert?.({ type: 'warning', messages: `Вариант ${option.name} уже есть` });
            return;
        }

        const newOption = { ...option, inputValue: '' };
        const newSelectedOptions = [...selectedOptions, newOption];
        setSelectedOptions(newSelectedOptions);
        onChange?.(newSelectedOptions);
        setIsOpen(false);
    };

    const handleInputChange = (optionId, value) => {
        const newSelectedOptions = selectedOptions.map((option) =>
            option.id === optionId ? { ...option, inputValue: value } : option
        );
        setSelectedOptions(newSelectedOptions);
        onChange?.(newSelectedOptions);
    };

    const handleDeleteOption = (optionId) => {
        const newSelectedOptions = selectedOptions.filter(option => option.id !== optionId);
        setSelectedOptions(newSelectedOptions);
        onChange?.(newSelectedOptions);
    };

    const handleSearch = () => {
        if (!url) return;

        const urlObj = new URL(url, window.location.origin);

        if (parentFieldName && parentValue) {
            urlObj.searchParams.set(parentFieldName, parentValue);
        }

        urlObj.searchParams.set("search", searchValue);

        dispatch(fetchFieldOptions({
            fieldName,
            url: urlObj.toString(),
            token
        }));
    };

    const handleClear = () => {
        setSearchValue("");

        const urlObj = new URL(initialUrl.current, window.location.origin);
        if (parentFieldName && parentValue) {
            urlObj.searchParams.set(parentFieldName, parentValue);
        }

        dispatch(fetchFieldOptions({
            fieldName,
            url: urlObj.toString(),
            token
        }));
    };

    return (
        <div className="multi-select-dropdown">
            <div className="multi-select-dropdown_container" onClick={toggleDropdown} ref={dropdownRef}>
                <div className="multi-select-dropdown_label">
                    {propertyLabel}
                </div>
            </div>

            {isOpen && (
                <div className="multi-select-dropdown_menu" onClick={(e) => e.stopPropagation()}>
                    <div className="dropdown_search">
                        <div className="dropdown_search-wrapper">
                            <input
                                className="dropdown_search-input"
                                value={searchValue}
                                type="text"
                                placeholder="Поиск"
                                onChange={(e) => setSearchValue(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleSearch();
                                    }
                                }}
                            />
                            {searchValue && (
                                <button className="dropdown_search-clear" onClick={handleClear}>
                                    <Cancel style={{fontSize: "18px"}}/>
                                </button>
                            )}
                        </div>
                        <IconButton Icon={Search} color="#3C50E0" onClick={(e) => {
                            e.stopPropagation();
                            handleSearch();
                        }}/>
                    </div>

                    <ul>
                        {options.map((option) => (
                            <li
                                key={option.id}
                                className="multi-select-dropdown_item"
                                onClick={(e) => handleOptionClick(e, option)}
                            >
                                {option.name}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="multi-select-dropdown_selected-options">
                {selectedOptions.map((option) => (
                    <div className="multi-select-dropdown_selected-options_wrapper" key={option.id}>
                        <div className="multi-select-dropdown_selected-options_title">
                            {option.name}
                        </div>
                        <div className="multi-select-dropdown_selected-options_item">
                            <input
                                className="multi-select-dropdown_selected-options_input"
                                value={option.inputValue}
                                type="number"
                                min="1"
                                onChange={(e) => handleInputChange(option.id, e.target.value)}
                                onWheel={(e) => e.preventDefault()}
                                placeholder={`Введите значение для ${option.name}`}
                            />
                            <button
                                className="multi-select-dropdown_selected-options_button"
                                onClick={() => handleDeleteOption(option.id)}
                            >
                                <Delete style={{ color: "#f44336" }} />
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// export const CreateFields = ({fieldStructure}) => {
//     const dispatch = useDispatch();
//     const [count, setCount] = useState('');
//     const [fields, setFields] = useState([]);
//
//     const handleAddFields = () => {
//         const num = parseInt(count, 10);
//         if (!isNaN(num) && num > 0) {
//             const newFields = Array.from({length: num}, () =>
//                 Object.keys(fieldStructure).reduce(
//                     (acc, key) => ({...acc, [key]: { ...fieldStructure[key], value: "" }}), {}
//                 )
//             )
//
//             setFields(prev => [...prev, ...newFields]);
//         } else {
//             dispatch(setShowAlert({type: "warning", messages: "Введите число, больше 0", context: "field-create"}));
//         }
//     }
//
//     const handleChange = (index, field, value) => {
//         setFields((prev) =>
//             prev.map((item, i) =>
//                 i === index ? { ...item, [field]: { ...item[field], value } } : item
//             )
//         )
//     }
//
//     const handleRemove = (index) => {
//         setFields((prev) => prev.filter((_, i) => i !== index));
//     };
//
//     return (
//         <div className={"create-fields"}>
//             <div className={"create-fields_container"}>
//                 <input
//                     className={"create-fields_input"}
//                     value={count}
//                     type={"number"}
//                     min={"1"}
//                     onChange={(e) => setCount(e.target.value)}
//                     placeholder={"Введите количество форм"}
//                 />
//                 <IconButton Icon={AddCircle} color={"#3C50E0"} onClick={handleAddFields}/>
//             </div>
//             <div className={"create-fields_items"}>
//                 {fields.map((form, index) => (
//                     <div className={"create-fields_items_group"}>
//                         {Object.keys(fieldStructure).map((fieldKey) => (
//                             <div className={"create-fields_items_field"}>
//                                 <input
//                                     key={fieldKey}
//                                     className={"create-fields_items_input"}
//                                     value={form[fieldKey].value}
//                                     type={form[fieldKey].type}
//                                     onChange={(e) => handleChange(index, fieldKey, e.target.value)}
//                                     placeholder={form[fieldKey].inputTitle}
//                                 />
//                                 <IconButton Icon={Delete} color={"#f44336"} onClick={() => handleRemove(index)}/>
//                             </div>
//                         ))}
//                     </div>
//                 ))}
//             </div>
//         </div>
//     )
// }

export const CreateFields = ({
                                 value = [],
                                 onChange,
                                 // подписи для инпутов — придут из Field
                                 nameLabel,
                                 positionLabel = "Должность",
                                 addButtonTitle = "Добавить",
                             }) => {
    const [mainInput, setMainInput] = useState(value[0] || { name: "", position: "" });
    const [additionalInputs, setAdditionalInputs] = useState(
        (value.slice(1) || []).map(v => v || { name: "", position: "" })
    );
    const lastEmitted = useRef([]);

    const normalizeRow = (obj) => ({
        name: (obj?.name ?? "").trim(),
        position: (obj?.position ?? "").trim(),
    });

    const emitChange = (main, additional) => {
        const values = [];
        const pushIfNotEmpty = (obj) => {
            const row = normalizeRow(obj);
            if (row.name || row.position) values.push(row);
        };
        pushIfNotEmpty(main);
        additional.forEach(pushIfNotEmpty);

        const same = JSON.stringify(values) === JSON.stringify(lastEmitted.current);
        if (!same) {
            onChange(values);
            lastEmitted.current = values;
        }
    };

    const handleMainChange = (key, val) => {
        const next = { ...mainInput, [key]: val };
        setMainInput(next);
        emitChange(next, additionalInputs);
    };

    const handleChangeAdditional = (index, key, val) => {
        const updated = [...additionalInputs];
        updated[index] = { ...updated[index], [key]: val };
        setAdditionalInputs(updated);
        emitChange(mainInput, updated);
    };

    const handleAddField = () => {
        const updated = [...additionalInputs, { name: "", position: "" }];
        setAdditionalInputs(updated);
        emitChange(mainInput, updated);
    };

    const handleRemoveField = (index) => {
        const updated = additionalInputs.filter((_, i) => i !== index);
        setAdditionalInputs(updated);
        emitChange(mainInput, updated);
    };

    return (
        <div className="create-fields">
            <div className="create-fields_main">
                <div className={"create-fields_inputs"}>
                    <input
                        className="create-fields_input"
                        type="text"
                        value={mainInput.name}
                        onChange={(e) => handleMainChange("name", e.target.value)}
                        placeholder={nameLabel}
                    />
                    <input
                        className="create-fields_input"
                        type="text"
                        value={mainInput.position}
                        onChange={(e) => handleMainChange("position", e.target.value)}
                        placeholder={positionLabel}
                    />
                </div>
                <IconButton Icon={AddCircle} color="#3C50E0" onClick={handleAddField} title={addButtonTitle}/>
            </div>

            {additionalInputs.map((val, index) => (
                <div className="create-fields_additional" key={index}>
                    <div className={"create-fields_inputs"}>
                        <input
                            className="create-fields_input"
                            type="text"
                            value={val.name}
                            onChange={(e) => handleChangeAdditional(index, "name", e.target.value)}
                            placeholder={nameLabel}
                        />
                        <input
                            className="create-fields_input"
                            type="text"
                            value={val.position}
                            onChange={(e) => handleChangeAdditional(index, "position", e.target.value)}
                            placeholder={positionLabel}
                        />
                    </div>
                    <IconButton Icon={Delete} color="#f44336" onClick={() => handleRemoveField(index)}/>
                </div>
            ))}
        </div>
    );
};

export const DoubleInput = ({
                                value = { name: "", position: "" },
                                onChange,
                                nameLabel = "ФИО",
                                positionLabel = "Должность",
                            }) => {
    const [input, setInput] = useState(value);
    const lastEmitted = useRef(value);

    const normalizeRow = (obj) => ({
        name: (obj?.name ?? "").trim(),
        position: (obj?.position ?? "").trim(),
    });

    const isRowEmpty = (row) => row.name === "" && row.position === "";

    const emitChange = (next) => {
        const row = normalizeRow(next);

        const payload = isRowEmpty(row) ? [] : [row];

        const same = JSON.stringify(payload) === JSON.stringify(lastEmitted.current);
        if (!same) {
            onChange(payload);
            lastEmitted.current = payload;
        }
    };

    const handleChange = (key, val) => {
        const next = { ...input, [key]: val };
        setInput(next);
        emitChange(next);
    };

    return (
        <div className="create-fields">
            <div className="create-fields_inputs">
                <input
                    className="create-fields_input"
                    type="text"
                    value={input.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    placeholder={nameLabel}
                />
                <input
                    className="create-fields_input"
                    type="text"
                    value={input.position}
                    onChange={(e) => handleChange("position", e.target.value)}
                    placeholder={positionLabel}
                />
            </div>
        </div>
    );
};

// export const CreateFields = ({ value = [], onChange, propertyLabel }) => {
//     const [mainInput, setMainInput] = useState(value[0] || "");
//     const [additionalInputs, setAdditionalInputs] = useState(value.slice(1) || []);
//     const lastEmitted = useRef([]);
//
//     const emitChange = (main, additional) => {
//         const values = [];
//
//         if (main.trim()) values.push(main.trim());
//         additional.forEach((v) => {
//             if (v.trim()) values.push(v.trim());
//         });
//
//         if (JSON.stringify(values) !== JSON.stringify(lastEmitted.current)) {
//             console.log("emitChange →", values);
//             onChange(values);
//             lastEmitted.current = values;
//         }
//     };
//
//     const handleMainChange = (val) => {
//         setMainInput(val);
//         emitChange(val, additionalInputs);
//     };
//
//     const handleChangeAdditional = (index, val) => {
//         const updated = [...additionalInputs];
//         updated[index] = val;
//         setAdditionalInputs(updated);
//         emitChange(mainInput, updated);
//     };
//
//     const handleAddField = () => {
//         const updated = [...additionalInputs, ""];
//         setAdditionalInputs(updated);
//         emitChange(mainInput, updated);
//     };
//
//     const handleRemoveField = (index) => {
//         const updated = additionalInputs.filter((_, i) => i !== index);
//         setAdditionalInputs(updated);
//         emitChange(mainInput, updated);
//     };
//
//     return (
//         <div className="create-fields">
//             <div className="create-fields_main">
//                 <input
//                     className="create-fields_input"
//                     type="text"
//                     value={mainInput}
//                     onChange={(e) => handleMainChange(e.target.value)}
//                     placeholder={propertyLabel}
//                 />
//                 <IconButton Icon={AddCircle} color="#3C50E0" onClick={handleAddField} />
//             </div>
//             {additionalInputs.map((val, index) => (
//                 <div className="create-fields_additional" key={index}>
//                     <input
//                         className="create-fields_input"
//                         type="text"
//                         value={val}
//                         onChange={(e) => handleChangeAdditional(index, e.target.value)}
//                         placeholder={propertyLabel}
//                     />
//                     <IconButton Icon={Delete} color="#f44336" onClick={() => handleRemoveField(index)} />
//                 </div>
//             ))}
//         </div>
//     );
// };

// export const CreateFields = ({ value = [], onChange, propertyLabel }) => {
//     const ensureTwo = (arr) => {
//         const base = Array.isArray(arr) ? arr.slice(0, 2) : [];
//         while (base.length < 2) base.push({});
//         return base.map((obj) => ({
//             name: obj?.name || "",
//             position: obj?.position || ""
//         }));
//     };
//
//     const [items, setItems] = useState(() => ensureTwo(value));
//
//     useEffect(() => {
//         setItems(ensureTwo(value));
//     }, [JSON.stringify(value)]);
//
//     const handleChange = (idx, field, val) => {
//         const updated = items.map((item, i) =>
//             i === idx ? { ...item, [field]: val } : item
//         );
//         setItems(updated);
//         onChange(updated);
//     };
//
//     return (
//         <div className="create-inputs">
//             {items.map((item, idx) => (
//                 <div className="create-inputs_row" key={idx}>
//                     <div className="create-inputs_field">
//                         <label>Имя</label>
//                         <input
//                             type="text"
//                             value={item.name}
//                             onChange={(e) => handleChange(idx, "name", e.target.value)}
//                             placeholder={`${propertyLabel || "Name"} #${idx + 1}`}
//                         />
//                     </div>
//                     <div className="create-inputs_field">
//                         <label>Должность</label>
//                         <input
//                             type="text"
//                             value={item.position}
//                             onChange={(e) => handleChange(idx, "position", e.target.value)}
//                             placeholder="Position"
//                         />
//                     </div>
//                 </div>
//             ))}
//         </div>
//     );
// };
export const EmergencyNameDropdown = ({emergencyOptions, handleSelect, error}) => {

    return (
        <Box className={"emergency_name_dropdown"}>
            <FormControl className={"emergency_name_dropdown_container"}>
                <InputLabel id="demo-simple-select-autowidth-label"> Наименование ЧС </InputLabel>
                <Select
                    id="demo-simple-select-autowidth"
                    className={"emergency_name_dropdown_container_select"}
                    labelId="demo-simple-select-autowidth-label"
                    autoWidth
                    label="Наименование ЧС"
                    onChange={handleSelect}
                    error={!!error}
                >
                    <MenuItem className={"emergency_name_dropdown_container_select_item"}>
                        Выберите наименование чс
                    </MenuItem>
                    {emergencyOptions.map((option) => (
                        <MenuItem
                            key={option.id}
                            value={option.id}
                            className={"emergency_name_dropdown_container_select_item"}
                        >
                            {option.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Box>
    )
}

export const TypeEmergencyDropdown = ({handleChange, error}) => {

    return (
        <Box className={"type_emergency_dropdown"}>
            <FormControl className={"type_emergency_dropdown_container"}>
                <InputLabel
                    id="demo-simple-select-autowidth-label"
                    className={"type_emergency_dropdown_container_inputlabel"}
                >
                    Вид донесения
                </InputLabel>
                <Select
                    id="demo-simple-select-autowidth"
                    className={"type_emergency_dropdown_container_select"}
                    labelId="demo-simple-select-autowidth-label"
                    autoWidth
                    label="Вид донесения"
                    onChange={handleChange}
                    error={!!error}
                >
                    <MenuItem
                        id={"agreement_emergency"}
                        className={"type_emergency_dropdown_container_select_item"}
                        value="agreement_emergency"
                    >
                        По договору
                    </MenuItem>
                    <MenuItem
                        id={"line_emergency"}
                        className={"type_emergency_dropdown_container_select_item"}
                        value="line_emergency"
                    >
                        По линии ЧС
                    </MenuItem>
                </Select>
            </FormControl>
        </Box>
    )
}

export const AgreementEmergencyDropdown = () => {
    const options = [
        {label: 'Шахта "Кузембаева"', id: "01"},
        {label:  "цех №6", id: "02"},
        {label: 'Шахта "Центральная"', id: "03"}
    ];

    return (
        <Box className={"agreement_emergency_dropdown"}>
            <FormControl className={"agreement_emergency_dropdown_container"}>
                <Autocomplete
                    className={"agreement_emergency_dropdown_container_select"}
                    options={options}
                    getOptionLabel={(option) => option.label}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            className={"agreement_emergency_dropdown_container_select_item"}
                            variant="outlined"
                            autoWidth
                            label="Объект по договору"
                        />
                    )}
                />
            </FormControl>
        </Box>
    )
}

export const ObjectOwnerDropdown = () => {
    const options = [
        { label:  'АО ТНК "КазХром"', id: "01" },
        { label: 'АО "Шубарколь комир"', id: "02" },
        { label:  'УД АО "Qarmet"', id: "03"}
    ]

    return (
        <Box className={"object_owner_dropdown"}>
            <FormControl className={"object_owner_dropdown_container"}>
                <Autocomplete
                    className={"object_owner_dropdown_container_select"}
                    options={options}
                    getOptionLabel={(option) => option.label}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            className={"object_owner_dropdown_container_select_item"}
                            label="Владелец объекта"
                            variant="outlined"
                        />
                    )}
                />
            </FormControl>
        </Box>
    )
}

export const TransportDropdown = ({transportOptions, handleSelect, error}) => {

    return (
        <Box className={"transport_dropdown"}>
            <FormControl className={"transport_dropdown_container"}>
                <InputLabel id="demo-simple-select-autowidth-label">Выбор транспорта</InputLabel>
                <Select
                    id="demo-simple-select-autowidth"
                    className={"transport_dropdown_container_select"}
                    labelId="demo-simple-select-autowidth-label"
                    autoWidth
                    label="Выбор транспорта"
                    onChange={handleSelect}
                    error={error}
                >
                    {transportOptions.map((option) => (
                        <MenuItem
                            key={option.id}
                            value={option.id}
                            className={"transport_dropdown_container_select_item"}
                        >
                            {option.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Box>
    )
}